import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, FileUp, Loader2, X } from 'lucide-react';
import type { ProfileInput } from '../../core/types';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/cn';
import { importLinkedInPdf, type ParsedProfile } from './pdfImport';

/**
 * The PDF import dialog.
 *
 * Built on the native `<dialog>` element, which brings the focus trap, the
 * backdrop, Escape-to-close, and `aria-modal` semantics with it. Hand-rolling
 * those on a `div` is where most custom modals lose keyboard users.
 *
 * The flow is deliberately three steps rather than one, because the parser is
 * heuristic: choose a file, review what was actually read, then apply. The
 * review step is not ceremony — it is the difference between "the import got my
 * job titles and companies the wrong way round" being visible or silently
 * corrupting the score.
 */

type Stage =
  | { name: 'idle' }
  | { name: 'parsing' }
  | { name: 'review'; parsed: ParsedProfile }
  | { name: 'error'; message: string };

export interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Receives only the fields the parser filled; the caller merges them. */
  onApply: (fields: Pick<ProfileInput, 'headline' | 'about' | 'experience' | 'skills'>) => void;
}

export function ImportDialog({ open, onClose, onApply }: ImportDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [stage, setStage] = useState<Stage>({ name: 'idle' });
  const [dragging, setDragging] = useState(false);

  // Drive the native dialog from the `open` prop rather than the `open`
  // attribute: only `showModal()` gives the focus trap and the backdrop.
  //
  // Both branches fall back to the attribute, because an environment without
  // `showModal()` has no `close()` either — jsdom is one. Guarding only the open
  // side would leave a dialog that can be shown and then throws on the way out.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      setStage({ name: 'idle' });
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setStage({
        name: 'error',
        message: 'That is not a PDF. Use More → Save to PDF on your LinkedIn profile.',
      });
      return;
    }
    setStage({ name: 'parsing' });
    try {
      setStage({ name: 'review', parsed: await importLinkedInPdf(file) });
    } catch (error) {
      setStage({
        name: 'error',
        message: error instanceof Error ? error.message : 'Something went wrong reading that PDF.',
      });
    }
  };

  const apply = () => {
    if (stage.name !== 'review') return;
    const { headline, about, experience, skills } = stage.parsed;
    onApply({ headline, about, experience, skills });
    onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      // The native close event fires for Escape and for backdrop dismissal, so
      // the parent's state stays in step however the dialog was closed.
      onClose={onClose}
      aria-labelledby="import-title"
      className={cn(
        // `m-auto` is not decoration. A modal `<dialog>` is centred by the UA
        // stylesheet's `margin: auto`, and Tailwind's preflight resets the margin
        // on every element — without this the dialog opens in the top-left corner.
        'm-auto w-[min(40rem,calc(100vw-2rem))] rounded-card border border-line bg-card p-0 text-strong shadow-lifted',
        'backdrop:bg-ink-950/50 backdrop:backdrop-blur-sm'
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-line p-5">
        <div>
          <h2 id="import-title" className="text-lg font-semibold">
            Import from your LinkedIn PDF
          </h2>
          <p className="mt-1 text-sm text-muted">
            Read in your browser. The file is never uploaded anywhere.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="-mt-1 -mr-2">
          <span className="sr-only-live">Close</span>
          <X aria-hidden="true" className="size-4" />
        </Button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto p-5">
        {stage.name === 'idle' || stage.name === 'error' ? (
          <div className="space-y-4">
            <ol className="space-y-1.5 text-sm text-muted">
              <li>
                <strong className="font-medium text-strong">1.</strong> Open your own LinkedIn
                profile.
              </li>
              <li>
                <strong className="font-medium text-strong">2.</strong> Click <em>More</em>, then{' '}
                <em>Save to PDF</em>.
              </li>
              <li>
                <strong className="font-medium text-strong">3.</strong> Drop that file below.
              </li>
            </ol>

            <label
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                void handleFile(event.dataTransfer.files[0]);
              }}
              className={cn(
                'flex cursor-pointer flex-col items-center gap-2 rounded-field border-2 border-dashed px-6 py-10 text-center transition-colors',
                dragging
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-950'
                  : 'border-line hover:bg-sunken'
              )}
            >
              {/* The same gradient tile the logo, the numbered steps and the
                  empty score panel use. It is here because this is the primary
                  action in the dialog and a grey glyph made it read as disabled
                  — and because the dialog was otherwise the one surface in the
                  app with no colour in it at all.

                  A tile rather than a tinted glyph: `gradient-text` is
                  documented display-only, and the audit only clears it at the
                  3:1 large-text threshold. White on the gradient clears the
                  strict 4.5:1 at every stop, so the colour goes behind the
                  glyph instead of into it. */}
              <span
                aria-hidden="true"
                className="grid size-11 place-items-center rounded-field text-white shadow-glow"
                style={{ backgroundImage: 'var(--gradient-cta)' }}
              >
                <FileUp className="size-5" />
              </span>
              <span className="text-sm font-medium text-strong">
                Drop your PDF here, or click to choose
              </span>
              <span className="text-xs text-subtle">PDF only, up to 10 MB</span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only-live"
                onChange={(event) => void handleFile(event.target.files?.[0])}
              />
            </label>

            {stage.name === 'error' ? (
              <p
                role="alert"
                className="rounded-field border border-critical-200 bg-critical-50 p-3 text-sm text-critical-700 dark:border-critical-600 dark:bg-critical-950 dark:text-critical-100"
              >
                {stage.message}
              </p>
            ) : null}

            <p className="text-xs text-subtle">
              Importing is a convenience, not a requirement — pasting each section in by hand gives
              a cleaner result, because the PDF export does not label which line is a job title and
              which is a company.
            </p>
          </div>
        ) : null}

        {stage.name === 'parsing' ? (
          <p className="flex items-center gap-2.5 py-8 text-sm text-muted">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            Reading the PDF…
          </p>
        ) : null}

        {stage.name === 'review' ? <ReviewStep parsed={stage.parsed} /> : null}
      </div>

      {stage.name === 'review' ? (
        <div className="flex flex-wrap justify-end gap-2 border-t border-line p-5">
          <Button variant="secondary" onClick={() => setStage({ name: 'idle' })}>
            Choose a different file
          </Button>
          <Button onClick={apply}>Use this — replaces what is in the form</Button>
        </div>
      ) : null}
    </dialog>
  );
}

function ReviewStep({ parsed }: { parsed: ParsedProfile }) {
  const rows: { label: string; value: string; empty: boolean }[] = [
    { label: 'Headline', value: parsed.headline, empty: !parsed.headline },
    {
      label: 'About',
      value: parsed.about
        ? `${parsed.about.slice(0, 180)}${parsed.about.length > 180 ? '…' : ''}`
        : '',
      empty: !parsed.about,
    },
    {
      label: 'Experience',
      value: parsed.experience
        .map(
          (entry) => `${entry.title || '(no title)'} · ${entry.organisation || '(no organisation)'}`
        )
        .join('\n'),
      empty: parsed.experience.length === 0,
    },
    { label: 'Skills', value: parsed.skills.join(', '), empty: parsed.skills.length === 0 },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-strong">Here is what we read</h3>

      <dl className="space-y-3">
        {rows.map((row) => (
          <div key={row.label} className="rounded-field border border-line bg-sunken p-3">
            <dt className="flex items-center justify-between text-xs font-semibold tracking-wide text-subtle uppercase">
              {row.label}
              {row.empty ? <Badge tone="important">nothing found</Badge> : null}
            </dt>
            <dd className="mt-1 text-sm whitespace-pre-wrap text-muted">
              {row.empty ? 'You will need to fill this in yourself.' : row.value}
            </dd>
          </div>
        ))}
      </dl>

      {parsed.warnings.length > 0 ? (
        <div className="rounded-field border border-important-200 bg-important-50 p-3 dark:border-important-600 dark:bg-important-950">
          <h4 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-important-700 uppercase dark:text-important-100">
            <AlertTriangle aria-hidden="true" className="size-3.5" />
            Check these
          </h4>
          <ul className="mt-1.5 list-disc space-y-1 pl-4 text-sm text-important-700 dark:text-important-100">
            {parsed.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
